import { useState, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  GitCompareArrows,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  MinusCircle,
  Upload,
  User,
  Cpu,
  Calendar,
  Layers,
  ChevronDown,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Button, Badge } from '@personic/shared'
import { api } from '@personic/shared'
import { useTestUsers, useTestUserProfile } from '../../../hooks/useMindMetaLTestUsers'

// ---------- Types ----------

interface DimensionComparison {
  dimension: string
  value_a: number | null
  value_b: number | null
  delta: number
  direction_match: boolean
  category: 'match' | 'close' | 'divergent'
}

interface ComparisonResult {
  dimensions: DimensionComparison[]
  fidelity_score: number
  match_count: number
  close_count: number
  divergent_count: number
  most_divergent: string[]
}

interface DistillationMetadata {
  canonical_id: string
  last_distillation_metadata: {
    provider: string
    model: string
    exchange_count: number | null
    tiers_processed: string[]
    timestamp: string
  } | null
  distillation_count: number
}

interface ParsedSpec {
  signals: Record<string, number>
  format_detected: string
  signal_count: number
}

// ---------- Dual Radar Chart ----------

const BIG_FIVE = [
  { key: 'openness', label: 'Openness', short: 'O' },
  { key: 'conscientiousness', label: 'Conscientiousness', short: 'C' },
  { key: 'extraversion', label: 'Extraversion', short: 'E' },
  { key: 'agreeableness', label: 'Agreeableness', short: 'A' },
  { key: 'neuroticism', label: 'Neuroticism', short: 'N' },
] as const

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

function DualRadar({
  sourceValues,
  distilledValues,
}: {
  sourceValues: Record<string, number>
  distilledValues: Record<string, number>
}) {
  const width = 320
  const center = width / 2
  const radius = 120
  const step = 360 / BIG_FIVE.length
  const rings = [0.2, 0.4, 0.6, 0.8, 1.0]

  const resolveBigFive = (signals: Record<string, number>, key: string): number => {
    // Try several path formats
    return (
      signals[`personality.bigFive.${key}`] ??
      signals[`personality.${key}`] ??
      signals[key] ??
      0
    )
  }

  const makePath = (signals: Record<string, number>) =>
    BIG_FIVE.map((t, i) => {
      const v = resolveBigFive(signals, t.key)
      const p = polarToCartesian(center, center, radius * v, i * step)
      return `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
    }).join(' ') + ' Z'

  const srcPath = makePath(sourceValues)
  const dstPath = makePath(distilledValues)

  return (
    <div className="flex flex-col items-center">
      <svg width={width} height={width} viewBox={`0 0 ${width} ${width}`}>
        {/* Rings */}
        {rings.map((r) => (
          <polygon
            key={r}
            points={BIG_FIVE.map((_, i) => {
              const p = polarToCartesian(center, center, radius * r, i * step)
              return `${p.x},${p.y}`
            }).join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth={1}
            className="text-warm-200"
          />
        ))}
        {/* Axes */}
        {BIG_FIVE.map((_, i) => {
          const end = polarToCartesian(center, center, radius, i * step)
          return <line key={i} x1={center} y1={center} x2={end.x} y2={end.y} stroke="currentColor" strokeWidth={1} className="text-warm-200" />
        })}
        {/* Source polygon (blue) */}
        <motion.path
          d={srcPath}
          fill="rgba(59,130,246,0.15)"
          stroke="#3b82f6"
          strokeWidth={2}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        />
        {/* Distilled polygon (copper) */}
        <motion.path
          d={dstPath}
          fill="rgba(184,115,51,0.15)"
          stroke="#b87333"
          strokeWidth={2}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        />
        {/* Labels */}
        {BIG_FIVE.map((t, i) => {
          const lp = polarToCartesian(center, center, radius + 22, i * step)
          return (
            <text key={t.key} x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="middle" fontSize={12} className="fill-midnight-700 font-medium">
              {t.short}
            </text>
          )
        })}
      </svg>
      {/* Legend */}
      <div className="flex gap-6 mt-2 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 bg-blue-500 inline-block rounded" />
          <span className="text-warm-600">Source Spec</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 inline-block rounded" style={{ backgroundColor: '#b87333' }} />
          <span className="text-warm-600">Distilled</span>
        </div>
      </div>
      {/* Values */}
      <div className="grid grid-cols-5 gap-3 mt-3 text-xs w-full">
        {BIG_FIVE.map((t) => {
          const sv = resolveBigFive(sourceValues, t.key)
          const dv = resolveBigFive(distilledValues, t.key)
          return (
            <div key={t.key} className="text-center">
              <div className="font-medium text-midnight-800">{t.label}</div>
              <div className="text-blue-600">{(sv * 100).toFixed(0)}%</div>
              <div style={{ color: '#b87333' }}>{(dv * 100).toFixed(0)}%</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------- Main Component ----------

export function ComparisonTab() {
  const [selectedUser, setSelectedUser] = useState('')
  const [sourceContent, setSourceContent] = useState('')
  const [isComparing, setIsComparing] = useState(false)
  const [isParsing, setIsParsing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [comparison, setComparison] = useState<ComparisonResult | null>(null)
  const [parsedSource, setParsedSource] = useState<ParsedSpec | null>(null)
  const [distillMeta, setDistillMeta] = useState<DistillationMetadata | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch test users
  const { data: testUsersData, isLoading: loadingUsers } = useTestUsers(1, 100)
  const testUsers = testUsersData?.test_users ?? []

  // Fetch selected profile
  const { data: profile, isLoading: loadingProfile } = useTestUserProfile(selectedUser)

  // Fetch distillation metadata when user selected
  const fetchMetadata = useCallback(async (canonicalId: string) => {
    try {
      const { data } = await api.get<DistillationMetadata>(
        `/api/mindmetal/v3/admin/test-users/${canonicalId}/distillation-metadata`
      )
      setDistillMeta(data)
    } catch {
      setDistillMeta(null)
    }
  }, [])

  const handleUserSelect = (canonicalId: string) => {
    setSelectedUser(canonicalId)
    setComparison(null)
    setParsedSource(null)
    setError(null)
    setDropdownOpen(false)
    if (canonicalId) fetchMetadata(canonicalId)
    else setDistillMeta(null)
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result
      if (typeof text === 'string') setSourceContent(text)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  const handleCompare = async () => {
    if (!selectedUser || !sourceContent.trim()) return
    setError(null)
    setIsComparing(true)
    setComparison(null)
    setParsedSource(null)

    try {
      // Step 1: Parse source spec
      setIsParsing(true)
      const parseRes = await api.post<ParsedSpec>('/api/mindmetal/v3/admin/parse-source-spec', {
        content: sourceContent,
        format: 'auto',
      })
      setParsedSource(parseRes.data)
      setIsParsing(false)

      // Step 2: Extract flat signals from the distilled profile
      const distilledSignals: Record<string, number> = {}
      if (profile) {
        const flattenSignals = (obj: Record<string, any>, prefix = '') => {
          for (const [k, v] of Object.entries(obj)) {
            const path = prefix ? `${prefix}.${k}` : k
            if (v && typeof v === 'object' && 'value' in v) {
              distilledSignals[path] = Number(v.value)
            } else if (v && typeof v === 'object' && !Array.isArray(v)) {
              flattenSignals(v, path)
            }
          }
        }
        if (profile.signals_universal) flattenSignals(profile.signals_universal)
        if (profile.signals_behavioral) flattenSignals(profile.signals_behavioral)
      }

      // Step 3: Compare
      const compareRes = await api.post<ComparisonResult>(
        '/api/mindmetal/v3/admin/compare-profiles',
        {
          signals_a: parseRes.data.signals,
          signals_b: distilledSignals,
        }
      )
      setComparison(compareRes.data)
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Comparison failed')
    } finally {
      setIsComparing(false)
      setIsParsing(false)
    }
  }

  const fidelityColor = (score: number) => {
    if (score >= 0.8) return 'text-emerald-600'
    if (score >= 0.6) return 'text-amber-600'
    return 'text-red-600'
  }

  const fidelityBg = (score: number) => {
    if (score >= 0.8) return 'bg-emerald-50'
    if (score >= 0.6) return 'bg-amber-50'
    return 'bg-red-50'
  }

  const categoryIcon = (cat: string) => {
    switch (cat) {
      case 'match':
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />
      case 'close':
        return <MinusCircle className="w-4 h-4 text-amber-500" />
      case 'divergent':
        return <AlertTriangle className="w-4 h-4 text-red-500" />
      default:
        return null
    }
  }

  const selectedTestUser = testUsers.find((u) => u.canonical_id === selectedUser)
  const meta = distillMeta?.last_distillation_metadata
  const signalCount = profile
    ? Object.keys(profile.signals_universal || {}).length + Object.keys(profile.signals_behavioral || {}).length
    : 0

  // Build flat signals for radar from profile
  const distilledFlatSignals: Record<string, number> = {}
  if (profile) {
    const flatten = (obj: Record<string, any>, prefix = '') => {
      for (const [k, v] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${k}` : k
        if (v && typeof v === 'object' && 'value' in v) {
          distilledFlatSignals[path] = Number(v.value)
        } else if (v && typeof v === 'object' && !Array.isArray(v)) {
          flatten(v, path)
        }
      }
    }
    if (profile.signals_universal) flatten(profile.signals_universal)
    if (profile.signals_behavioral) flatten(profile.signals_behavioral)
  }

  return (
    <div className="space-y-6">
      {/* Input Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitCompareArrows className="w-5 h-5 text-copper-600" />
            Round-Trip Fidelity Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-warm-500 mb-6">
            Select a test user's distilled profile and paste the original synthetic personality spec to compare round-trip fidelity.
          </p>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left: Test User Selector */}
            <div>
              <label className="block text-sm font-medium text-midnight-700 mb-2">
                Distilled Profile
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full px-4 py-3 border border-warm-300 rounded-xl bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-copper-400 focus:border-copper-400 outline-none transition-all"
                >
                  <span className={selectedUser ? 'text-midnight-900' : 'text-warm-400'}>
                    {selectedTestUser
                      ? `${selectedTestUser.canonical_id} — ${selectedTestUser.display_name}`
                      : 'Select a test user...'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-warm-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {dropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-warm-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {loadingUsers ? (
                      <div className="p-4 text-sm text-warm-500">Loading...</div>
                    ) : testUsers.length === 0 ? (
                      <div className="p-4 text-sm text-warm-500">No test users found</div>
                    ) : (
                      testUsers.map((u) => (
                        <button
                          key={u.canonical_id}
                          type="button"
                          onClick={() => handleUserSelect(u.canonical_id)}
                          className={`w-full px-4 py-2.5 text-left text-sm hover:bg-warm-50 transition-colors flex items-center justify-between ${
                            u.canonical_id === selectedUser ? 'bg-copper-50 text-copper-700' : 'text-midnight-700'
                          }`}
                        >
                          <span>
                            <span className="font-mono font-medium">{u.canonical_id}</span>
                            <span className="text-warm-500 ml-2">— {u.display_name}</span>
                          </span>
                          <span className="text-xs text-warm-400 capitalize">{u.coverage_level}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Profile metadata card */}
              {selectedUser && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 bg-warm-50 rounded-xl p-4 space-y-2.5"
                >
                  {loadingProfile ? (
                    <div className="text-sm text-warm-500">Loading profile...</div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-warm-400" />
                        <span className="font-medium text-midnight-700">
                          {selectedTestUser?.display_name}
                        </span>
                        <span className="text-warm-400 font-mono text-xs">{selectedUser}</span>
                      </div>
                      {meta && (
                        <>
                          <div className="flex items-center gap-2 text-sm text-warm-600">
                            <Cpu className="w-4 h-4 text-warm-400" />
                            <span>
                              {meta.provider} / {meta.model}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-warm-600">
                            <Calendar className="w-4 h-4 text-warm-400" />
                            <span>{new Date(meta.timestamp).toLocaleDateString()}</span>
                          </div>
                        </>
                      )}
                      <div className="flex items-center gap-2 text-sm text-warm-600">
                        <Layers className="w-4 h-4 text-warm-400" />
                        <span>
                          {signalCount} signal tiers &middot;{' '}
                          <span className="capitalize">{profile?.coverage_level || 'minimal'}</span> coverage
                        </span>
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </div>

            {/* Right: Source Spec Input */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-midnight-700">
                  Source Spec (Original Synthetic Personality)
                </label>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1 text-xs text-copper-600 hover:text-copper-700 transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Upload JSON
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,.md,.txt"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>
              <textarea
                value={sourceContent}
                onChange={(e) => setSourceContent(e.target.value)}
                placeholder={`Paste JSON or Markdown source spec...\n\n{\n  "personality.bigFive.openness": 0.82,\n  "personality.bigFive.conscientiousness": 0.78,\n  ...\n}\n\nOr markdown:\n- openness: 0.82\n- conscientiousness: 0.78`}
                rows={12}
                className="w-full px-4 py-3 border border-warm-300 rounded-xl bg-white font-mono text-sm text-midnight-900 focus:ring-2 focus:ring-copper-400 focus:border-copper-400 outline-none transition-all resize-y"
              />
              {parsedSource && (
                <div className="mt-2 text-xs text-warm-500">
                  Detected: <span className="font-medium">{parsedSource.format_detected}</span> &middot; {parsedSource.signal_count} signals extracted
                </div>
              )}
            </div>
          </div>

          <div className="mt-6">
            <Button
              onClick={handleCompare}
              isLoading={isComparing}
              disabled={!selectedUser || !sourceContent.trim() || loadingProfile}
              leftIcon={<BarChart3 className="w-4 h-4" />}
            >
              {isParsing ? 'Parsing spec...' : isComparing ? 'Comparing...' : 'Compare'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Results */}
      {comparison && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          {/* Score Cards */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-copper-600" />
                Fidelity Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`${fidelityBg(comparison.fidelity_score)} rounded-xl p-4 text-center`}>
                  <div className={`text-3xl font-bold ${fidelityColor(comparison.fidelity_score)}`}>
                    {(comparison.fidelity_score * 100).toFixed(0)}%
                  </div>
                  <div className="text-xs text-warm-500 mt-1">Fidelity Score</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-emerald-700">{comparison.match_count}</div>
                  <div className="text-xs text-emerald-600 mt-1">Matches (&lt;0.10)</div>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-amber-700">{comparison.close_count}</div>
                  <div className="text-xs text-amber-600 mt-1">Close (&lt;0.25)</div>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-red-700">{comparison.divergent_count}</div>
                  <div className="text-xs text-red-600 mt-1">Divergent</div>
                </div>
              </div>

              {/* Most divergent badges */}
              {comparison.most_divergent.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-midnight-700 mb-2">Biggest Mismatches</h4>
                  <div className="flex flex-wrap gap-2">
                    {comparison.most_divergent.map((dim) => (
                      <Badge key={dim} variant="error" size="sm">
                        {dim}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Radar overlay */}
          {parsedSource && Object.keys(distilledFlatSignals).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Big Five Overlay</CardTitle>
              </CardHeader>
              <CardContent className="flex justify-center">
                <DualRadar
                  sourceValues={parsedSource.signals}
                  distilledValues={distilledFlatSignals}
                />
              </CardContent>
            </Card>
          )}

          {/* Per-dimension table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Per-Dimension Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-warm-200">
                      <th className="text-left py-2 px-3 text-warm-500 font-medium">Dimension</th>
                      <th className="text-center py-2 px-3 text-warm-500 font-medium">Source</th>
                      <th className="text-center py-2 px-3 text-warm-500 font-medium">Distilled</th>
                      <th className="text-center py-2 px-3 text-warm-500 font-medium">Delta</th>
                      <th className="text-center py-2 px-3 text-warm-500 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.dimensions.map((dim) => (
                      <tr key={dim.dimension} className="border-b border-warm-100 hover:bg-warm-50">
                        <td className="py-2 px-3 font-mono text-xs text-midnight-700">{dim.dimension}</td>
                        <td className="py-2 px-3 text-center">
                          {dim.value_a != null ? (
                            <span className="font-medium">{dim.value_a.toFixed(2)}</span>
                          ) : (
                            <span className="text-warm-400">--</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {dim.value_b != null ? (
                            <span className="font-medium">{dim.value_b.toFixed(2)}</span>
                          ) : (
                            <span className="text-warm-400">--</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span
                            className={
                              dim.delta < 0.1
                                ? 'text-emerald-600'
                                : dim.delta < 0.25
                                  ? 'text-amber-600'
                                  : 'text-red-600'
                            }
                          >
                            {dim.delta.toFixed(3)}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {categoryIcon(dim.category)}
                            <span className="text-xs capitalize">{dim.category}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  )
}
